import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from '../auth/AuthProvider';
import { isTrustedIdpInitiatedIss } from '../auth/idpInitiatedLogin';
import { oktaAuth } from '../auth/oktaConfig';
import styles from './Login.module.css';

const LOGO_SRC = '/battle-plan-logo.jpg';
const configuredIssuer = import.meta.env.VITE_OKTA_ISSUER;

export function LoginPage() {
  const state = useAuthState();
  const [params] = useSearchParams();
  const next = params.get('next') || '/builders';
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const idpRedirectStarted = useRef(false);

  const startOktaSignIn = useCallback(async () => {
    setErr(null);
    setPending(true);
    try {
      const uri = next.startsWith('/') ? next : '/builders';
      await oktaAuth.signInWithRedirect({ originalUri: uri });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Sign-in failed');
      setPending(false);
      idpRedirectStarted.current = false;
    }
  }, [next]);

  const idpInitiated = isTrustedIdpInitiatedIss(params.get('iss'), configuredIssuer);

  useEffect(() => {
    if (state.status === 'loading' || state.status === 'authenticated') return;
    if (!idpInitiated || idpRedirectStarted.current) return;
    idpRedirectStarted.current = true;
    void startOktaSignIn();
  }, [state.status, idpInitiated, startOktaSignIn]);

  if (state.status === 'authenticated') {
    return <Navigate to={next.startsWith('/') ? next : '/builders'} replace />;
  }

  const showRedirecting = pending || (idpInitiated && state.status !== 'loading');

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <img className={styles.logo} src={LOGO_SRC} alt="Operation Battle Plan" />
        <div className={styles.brandTitle}>Operation Battle Plan</div>
        <div className={styles.motto}>Love to Win · Hate to Lose</div>

        <div className={styles.box}>
          <h3 className={styles.boxHeading}>Sign In</h3>
          <div className={styles.form}>
            {err ? <div className={styles.error}>{err}</div> : null}
            <button
              type="button"
              className={styles.submit}
              disabled={state.status === 'loading' || showRedirecting}
              onClick={() => void startOktaSignIn()}
            >
              {showRedirecting ? 'Redirecting to Okta…' : 'Sign In With Okta'}
            </button>
          </div>
        </div>

        <div className={styles.footer}>
          Sole Construction Partners © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
